/**
 * Danish catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 2.
 *
 * `dir: 'ltr'`. Native-grade Danish at a mathematician's register. **Warrant → «Hjemmel»** —
 * the established Scandinavian rendering of Toulmin's warrant. W/M grades stay Latin
 * (`::numbering-system/latn`). CLDR cardinal categories are one / other, so `frameActive`
 * declares exactly those (plus an explicit `=0`). Per INV-STABLE-ID a wrong translation is a
 * presentation concern only — every verdict stays fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const DA_CATALOG = {
    locale: 'da',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Undersøgelse',
        [P2.sectionClaims]: 'Påstande',
        [P2.sectionIntegrity]: 'Integritet',
        [P2.axisWarrantLabel]: 'Hjemmel',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Empirisk',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Definition} interp {Fortolkning} testable {Testbar} tested {Testet} refuted {Gendrevet} program {Forskningsprogram} philos {Filosofisk} other {Ukendt}}',
        [P2.axisModalLabel]: 'Modal',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Styrkeniveau {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Udforskbar',
        [P2.stateRefuted]: 'Gendrevet',
        [P2.stateOutside]: 'Uden for området',
        [P2.verdictConflict]: 'I konflikt med {constraints}',
        [P2.verdictOutside]: 'Kræver {missing}',
        [P2.lossRegisterPlain]: 'Forenklet til almindeligt sprog — en del af præcisionen udelades.',
        [P2.lossRegisterKid]: 'Forenklet for yngre læsere — mange detaljer udelades.',
        [P2.lossProfileDetail]: 'Den fulde profil Hjemmel / Empirisk / Modal er sammenfattet her.',
        [P2.lossConflictDetail]: 'Konfliktdetaljerne er forkortet i denne visning.',
        [P2.laneVerified]: 'Verificeret',
        [P2.laneUnverified]: 'Ikke verificeret',
        [P2.laneConflict]: 'Konflikt påvist',
        [P2.constraintLogic]: 'Logik',
        [P2.constraintLightBends]: 'Lys bøjer i et tyngdefelt',
        [P2.constraintParallelPostulate]: 'Parallelpostulatet',
        [P2.claimScalarGravity]: 'Skalar tyngdekraftsteori',
        [P2.claimNonEuclidean]: 'Ikke-euklidisk geometri',
        [P2.claimSubstrateSr]: 'Substratfortolkning af den specielle relativitetsteori',
        [P2.edgeExtObservation]: 'Ekstern observation',
        [P2.edgeResourceSample]: 'Delt beregningsressource',
        [P2.edgeHeuristicSample]: 'Heuristisk vejledning',
        [P2.edgeSharedSample]: 'Fælles grundlag',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Begrænsninger',
        [P2.sectionEdges]: 'Kanter',
        [P2.sectionFrame]: 'Ramme',
        [P2.sectionExposure]: 'Eksponering',
        [P2.statusAdoptedFormal]: 'Vedtaget (formel)',
        [P2.statusAdoptedInterpretive]: 'Vedtaget (fortolkende)',
        [P2.statusSuspended]: 'Suspenderet',
        [P2.edgeEffectConstraint]: 'Danner en begrænsning (kan gendrive)',
        [P2.edgeEffectResource]: 'Ressource — ingen virkning på domme',
        [P2.edgeEffectHeuristic]: 'Heuristik — ingen virkning på domme',
        [P2.edgeEffectShared]: 'Fælles grundlag — ingen virkning på domme',
        [P2.frameActive]: '{count, plural, =0 {Ingen aktive begrænsninger} one {# aktiv begrænsning} other {# aktive begrænsninger}}',
        [P2.exposureNone]: 'Ingen fortolkende afhængigheder',
        [P2.exposureDep]: 'Afhænger af {id}',
        [P2.laneTrackMachine]: 'Maskine',
        [P2.laneTrackExpert]: 'Ekspert',
        [P2.laneTrackCommunity]: 'Fællesskab',
        [P2.expertAudited]: 'Auditeret',
        [P2.expertInProgress]: 'I gang',
        [P2.expertNotReviewed]: 'Ikke gennemgået',
        [P2.communityFlags]: 'Der findes markeringer',
        [P2.communityNone]: 'Ingen markeringer',
        [P2.controlChallenge]: 'Anfægt',
        [P2.controlActivate]: 'Aktivér',
        [P2.controlMakeInterpretive]: 'Gør fortolkende',
        [P2.controlImportEdge]: 'Importér kant',
        [P2.controlPromote]: 'Forfrem',
        [P2.controlUndo]: 'Fortryd',
        [P2.controlRedo]: 'Gentag',
        [P2.controlRegister]: 'Registrér',
        [P2.promoteRejected]: 'Forfremmelse afvist',
        [P2.promoteAccepted]: 'Forfremmelse accepteret',
        [P2.promoteReasonRefutationTerminal]: 'Et bevis kan ikke ophæve en gendrivelse.',
        [P2.promoteReasonKeystone]: 'En hjørnesten-forfremmelse kræver empirisk støtte.',
        [P2.promoteReasonEmpiricalNotFixed]: 'Den empiriske type er ikke fastlagt.',
        [P2.promoteReasonModalNotFixed]: 'Den modale grad er ikke fastlagt.',
        [P2.promoteReasonWarrantNotImproved]: 'Hjemmelsgraden er ikke forbedret.',
        [P2.evidenceComputation]: "{engine} beregnede {op} = {result} under {config}",
        [P2.lossNumericRounding]: "Resultat afrundet til {dp} decimaler",
    },
};
//# sourceMappingURL=da.js.map